import type { AnyProps } from './registry'

/** Marks an element as a mount target and names the component to render. */
export const COMPONENT_ATTR = 'data-cv'

/** Optional JSON blob for props that don't fit in flat attributes. */
export const PROPS_ATTR = 'data-cv-props'

const NUMERIC = /^-?(?:\d+|\d*\.\d+)(?:e[-+]?\d+)?$/i

/**
 * Attributes the runtime owns. Anything matching is never passed to a
 * component, which keeps `data-cv-portal`, `data-cv-props` etc. out of props.
 */
function isReservedAttr(name: string): boolean {
  return name === COMPONENT_ATTR || name.startsWith(`${COMPONENT_ATTR}-`)
}

/** `data-redirect-to` -> `redirectTo` */
export function toCamelCase(attributeName: string): string {
  return attributeName
    .replace(/^data-/, '')
    .replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase())
}

/**
 * Coerce an attribute string into a usable JS value.
 *
 * Marketing writes these by hand in the Webflow designer, so the rules are
 * deliberately forgiving:
 *   data-compact           -> true   (valueless attribute = boolean flag)
 *   data-compact="false"   -> false
 *   data-count="3"         -> 3
 *   data-plan='{"id":1}'   -> { id: 1 }
 *   data-label="3 seats"   -> "3 seats"
 */
export function coerceValue(raw: string): unknown {
  if (raw === '') return true
  const value = raw.trim()

  if (value === 'true') return true
  if (value === 'false') return false
  if (value === 'null') return null
  if (value === 'undefined') return undefined
  if (NUMERIC.test(value)) return Number(value)

  const first = value[0]
  if (first === '{' || first === '[') {
    try {
      return JSON.parse(value)
    } catch {
      // Malformed JSON is far more likely to be a string that happens to start
      // with a brace than a typo worth throwing over. Pass it through.
      return raw
    }
  }

  return raw
}

/**
 * Build a props object from an element's `data-*` attributes.
 *
 * `data-cv-props` is parsed first so individual attributes can override it,
 * which makes the common "shared defaults plus one tweak" case work.
 */
export function readProps(element: Element): AnyProps {
  const props: AnyProps = {}

  const blob = element.getAttribute(PROPS_ATTR)
  if (blob) {
    try {
      const parsed: unknown = JSON.parse(blob)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        Object.assign(props, parsed)
      } else {
        console.warn(`[codeverse] ${PROPS_ATTR} must be a JSON object; ignoring`, blob)
      }
    } catch (error) {
      console.warn(`[codeverse] could not parse ${PROPS_ATTR}`, blob, error)
    }
  }

  for (const attr of element.attributes) {
    if (!attr.name.startsWith('data-') || isReservedAttr(attr.name)) continue
    props[toCamelCase(attr.name)] = coerceValue(attr.value)
  }

  return props
}

/** Read the component name a mount target is asking for. */
export function readComponentName(element: Element): string | null {
  const name = element.getAttribute(COMPONENT_ATTR)?.trim()
  return name ? name : null
}
